import React, { useMemo } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import { CATALOG_COVER_ASPECT } from '@/constants/catalogMedia';

function Block({ style, color }) {
  return <View style={[{ backgroundColor: color, borderRadius: 10 }, style]} />;
}

/**
 * Layout-shaped placeholder shown while catalog detail pages fetch their payload.
 * Reserves the same vertical space as the hydrated page so a centered spinner
 * does not collapse #root and blow out CLS on prompts/tools detail routes.
 */
export default function CatalogDetailPageSkeleton({ variant = 'prompt' }) {
  const { colors } = useTheme();
  const { isXS, isMD, isXL } = useResponsiveness();
  const block = colors.solid_color_tertiary;

  const styles = useMemo(() => StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.solid_color_primary },
    scroll: { flex: 1, width: '100%' },
    scrollContent: { width: '100%', paddingBottom: 80 },
    inner: {
      padding: variant === 'prompt' ? 15 : (isXS ? 15 : 24),
      paddingTop: variant === 'prompt' ? 5 : (isXS ? 18 : 28),
      maxWidth: variant === 'prompt' ? 800 : (isXL ? 1500 : 1280),
      alignSelf: 'center',
      width: '100%',
      gap: variant === 'prompt' ? 18 : 0,
    },
    hero: {
      width: '100%',
      minHeight: variant === 'prompt' ? 148 : (isXS ? 160 : 190),
      borderRadius: variant === 'prompt' ? 14 : 0,
      marginBottom: variant === 'prompt' ? 0 : (isXS ? 12 : 16),
    },
    media: {
      width: '100%',
      aspectRatio: CATALOG_COVER_ASPECT,
      borderRadius: 14,
    },
    workArea: {
      flexDirection: isMD ? 'column' : 'row',
      gap: isMD ? 20 : 24,
      marginTop: variant === 'tool' ? (isXS ? 8 : 12) : 0,
    },
    column: {
      flex: isMD ? undefined : 1,
      width: isMD ? '100%' : undefined,
      gap: 12,
    },
    card: {
      minHeight: variant === 'tool' ? (isMD ? 280 : 360) : 260,
      borderRadius: 14,
    },
    line: {
      height: 14,
      borderRadius: 6,
    },
  }), [colors.solid_color_primary, isMD, isXL, isXS, variant]);

  return (
    <View style={styles.container} accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        <View style={styles.inner}>
          <Block style={styles.hero} color={block} />
          {variant === 'prompt' ? <Block style={styles.media} color={block} /> : null}
          {variant === 'tool' ? (
            <View style={styles.workArea}>
              <View style={styles.column}>
                <Block style={{ width: 120, height: 18 }} color={block} />
                <Block style={styles.card} color={block} />
              </View>
              {!isMD ? (
                <View style={styles.column}>
                  <Block style={{ width: 96, height: 18 }} color={block} />
                  <Block style={[styles.card, { minHeight: 360 }]} color={block} />
                </View>
              ) : null}
            </View>
          ) : (
            <Block style={styles.card} color={block} />
          )}
          {variant === 'prompt' ? (
            <>
              <Block style={[styles.line, { width: '42%' }]} color={block} />
              <Block style={[styles.line, { width: '78%' }]} color={block} />
            </>
          ) : null}
        </View>
      </ScrollView>
    </View>
  );
}
